const MAX_OUTPUT_HEIGHT=720;
const MAX_IN_MEMORY_BYTES=250*1024*1024;
const INIT_TIMEOUT_MS=45000;
const STALL_TIMEOUT_MS=90000;

export type VideoPreparation={file:File;compressed:boolean;reason?:'not-video'|'unsupported'|'too-large'|'canceled'|'stalled'|'failed'};

export function isMovVideo(file:Pick<File,'name'|'type'>){
 return file.type.toLowerCase()==='video/quicktime'||file.name.toLowerCase().endsWith('.mov');
}

function timeout<T>(promise:Promise<T>,milliseconds:number){
 return new Promise<T>((resolve,reject)=>{const timer=setTimeout(()=>reject(new Error('timeout')),milliseconds);promise.then(value=>{clearTimeout(timer);resolve(value)},cause=>{clearTimeout(timer);reject(cause)})});
}

export async function compressVideoTo720p(file:File,onProgress?:(value:number)=>void,signal?:AbortSignal):Promise<VideoPreparation>{
 if(!file.type.startsWith('video/'))return {file,compressed:false,reason:'not-video'};
 if(file.size>MAX_IN_MEMORY_BYTES)return {file,compressed:false,reason:'too-large'};
 if(!('VideoEncoder'in globalThis)||!('VideoDecoder'in globalThis))return {file,compressed:false,reason:'unsupported'};
 try{
  const {ALL_FORMATS,BlobSource,BufferTarget,Conversion,Input,Mp4OutputFormat,Output,Quality}=await import('mediabunny');
  const input=new Input({source:new BlobSource(file),formats:ALL_FORMATS});
  try{
   if(!(await timeout(input.canRead(),INIT_TIMEOUT_MS)))return {file,compressed:false,reason:'unsupported'};
   const target=new BufferTarget(),output=new Output({format:new Mp4OutputFormat({fastStart:'in-memory'}),target});
   const conversion=await timeout(Conversion.init({input,output,tracks:'primary',video:async(track)=>({codec:'avc',height:Math.min(MAX_OUTPUT_HEIGHT,await track.getDisplayHeight()),quality:new Quality('medium'),forceTranscode:true,keyFrameInterval:3,hardwareAcceleration:'prefer-hardware'}),audio:{codec:'aac',quality:new Quality('medium')},showWarnings:false}),INIT_TIMEOUT_MS);
   if(!conversion.isValid)return {file,compressed:false,reason:'unsupported'};
   let lastProgress=Date.now(),stalled=false,canceled=false;
   conversion.onProgress=p=>{lastProgress=Date.now();onProgress?.(Math.max(0,Math.min(1,p)))};
   const abort=()=>{canceled=true;void conversion.cancel()};signal?.addEventListener('abort',abort,{once:true});
   const watchdog=setInterval(()=>{if(Date.now()-lastProgress>=STALL_TIMEOUT_MS){stalled=true;void conversion.cancel()}},1000);
   try{await conversion.execute()}catch(cause){if(canceled||signal?.aborted)return {file,compressed:false,reason:'canceled'};if(stalled)return {file,compressed:false,reason:'stalled'};throw cause}finally{clearInterval(watchdog);signal?.removeEventListener('abort',abort)}
   if(!target.buffer)return {file,compressed:false,reason:'failed'};
   return {file:new File([target.buffer],`${file.name.replace(/\.[^.]+$/,'')||'video'}-720p.mp4`,{type:'video/mp4',lastModified:Date.now()}),compressed:true};
  }finally{input.dispose()}
 }catch{return {file,compressed:false,reason:signal?.aborted?'canceled':'failed'}}
}
